import React, { useState, ReactNode, useRef, useEffect } from "react";

interface TooltipProps {
  icon: React.ReactNode;
  text?: ReactNode;
  position?: "top" | "bottom" | "right" | "left";
  isActive?: boolean;
  maxWidth?: number;
  delay?: number;
  component?: ReactNode;
}

export default function Tooltip({
  icon,
  text,
  component,
  position = "top",
  isActive,
  maxWidth = 250,
  delay = 0,
}: TooltipProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState(position);
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (isOpen && containerRef.current && tooltipRef.current) {
      const container = containerRef.current.getBoundingClientRect();
      const tooltip = tooltipRef.current.getBoundingClientRect();
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight,
        scrollY: window.scrollY,
        scrollX: window.scrollX,
      };

      let newPosition = position;
      const padding = 10;

      if (position === "top" && container.top - tooltip.height < padding) {
        newPosition = "bottom";
      } else if (
        position === "bottom" &&
        container.bottom + tooltip.height > viewport.height - padding
      ) {
        newPosition = "top";
      } else if (
        position === "left" &&
        container.left - tooltip.width < padding
      ) {
        newPosition = "right";
      } else if (
        position === "right" &&
        container.right + tooltip.width > viewport.width - padding
      ) {
        newPosition = "left";
      }

      setTooltipPosition(newPosition);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isOpen, position]);

  const handleMouseEnter = () => {
    if (delay) {
      timeoutRef.current = setTimeout(() => setIsOpen(true), delay);
    } else {
      setIsOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsOpen(false);
  };

  const getPositionClasses = () => {
    const baseClasses = "absolute z-50 ";
    switch (tooltipPosition) {
      case "top":
        return baseClasses + "bottom-full mb-2 left-1/2 -translate-x-1/2";
      case "bottom":
        return baseClasses + "top-full mt-2 left-1/2 -translate-x-1/2";
      case "right":
        return baseClasses + "left-full ml-2 top-1/2 -translate-y-1/2";
      case "left":
        return baseClasses + "right-full mr-2 top-1/2 -translate-y-1/2";
      default:
        return baseClasses + "bottom-full mb-2 left-1/2 -translate-x-1/2";
    }
  };

  return (
    <div
      className="relative inline-flex items-center"
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        type="button"
        className={`inline-flex items-center ${
          isActive
            ? "text-base-blue-100"
            : "text-gray-600 hover:text-base-gray-100"
        }`}
      >
        {icon}
      </button>
      {isOpen && (
        <div
          ref={tooltipRef}
          className={`
            ${getPositionClasses()}
            bg-gray-800 text-white rounded-md text-xs p-2
            shadow-lg
          `}
          style={{
            maxWidth: `${maxWidth}px`,
            width: "max-content",
          }}
        >
          {text ? <div>{text}</div> : component}
          <div
            className="absolute w-2 h-2 bg-gray-800 transform rotate-45"
            style={{
              ...getArrowStyles(tooltipPosition),
            }}
          />
        </div>
      )}
    </div>
  );
}

const getArrowStyles = (position: string): React.CSSProperties => {
  switch (position) {
    case "top":
      return { bottom: "-4px", left: "50%", marginLeft: "-4px" };
    case "bottom":
      return { top: "-4px", left: "50%", marginLeft: "-4px" };
    case "left":
      return { right: "-4px", top: "50%", marginTop: "-4px" };
    case "right":
      return { left: "-4px", top: "50%", marginTop: "-4px" };
    default:
      return { bottom: "-4px", left: "50%", marginLeft: "-4px" };
  }
};
